import { useEffect, useState } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";

function useReport(kind) {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try { const r = await api.get(`/reports/${kind}`); setData(r.data); }
            catch (e) { toast.error(formatApiError(e)); }
        })();
    }, [kind]);
    return data;
}

export function Payables() {
    const d = useReport("payables");
    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Payables" sub="Everyone I owe. Grouped by ledger, most recent movement first." />
            <div className="p-8">
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="payables-table">
                        <thead><tr><th>Party</th><th>Group</th><th>GSTIN</th><th className="text-right">We owe</th></tr></thead>
                        <tbody>
                            {d?.items?.length ? d.items.map((i) => (
                                <tr key={i.ledger.id}>
                                    <td><Link to={`/ledgers/${i.ledger.id}`} className="hover:underline" data-testid={`payable-${i.ledger.id}`}>{i.ledger.name}</Link></td>
                                    <td className="text-xs text-stone-500">{i.ledger.group_name}</td>
                                    <td className="text-xs text-stone-500">{i.ledger.gstin || "—"}</td>
                                    <td className="se-num">₹ {inr(i.balance)}</td>
                                </tr>
                            )) : <tr><td colSpan="4" className="text-stone-500 py-8 text-center">No payables.</td></tr>}
                        </tbody>
                        {d?.items?.length ? (
                            <tfoot><tr><td colSpan="3" className="font-medium">Total</td><td className="se-num font-medium">₹ {inr(d.total)}</td></tr></tfoot>
                        ) : null}
                    </table>
                </div>
            </div>
        </>
    );
}

export function Receivables() {
    const d = useReport("receivables");
    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Receivables" sub="Everyone who owes me — including B2C individuals with advances / invoices." />
            <div className="p-8">
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="receivables-table">
                        <thead><tr><th>Party</th><th>Group</th><th>GSTIN</th><th className="text-right">They owe</th></tr></thead>
                        <tbody>
                            {d?.items?.length ? d.items.map((i) => (
                                <tr key={i.ledger.id}>
                                    <td><Link to={`/ledgers/${i.ledger.id}`} className="hover:underline" data-testid={`receivable-${i.ledger.id}`}>{i.ledger.name}</Link></td>
                                    <td className="text-xs text-stone-500">{i.ledger.group_name}</td>
                                    <td className="text-xs text-stone-500">{i.ledger.gstin || "—"}</td>
                                    <td className="se-num">₹ {inr(i.balance)}</td>
                                </tr>
                            )) : <tr><td colSpan="4" className="text-stone-500 py-8 text-center">No receivables.</td></tr>}
                        </tbody>
                        {d?.items?.length ? (
                            <tfoot><tr><td colSpan="3" className="font-medium">Total</td><td className="se-num font-medium">₹ {inr(d.total)}</td></tr></tfoot>
                        ) : null}
                    </table>
                </div>
            </div>
        </>
    );
}

export function Expenses() {
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [kind, setKind] = useState("");
    const [dept, setDept] = useState("");
    const [loc, setLoc] = useState("");
    const [data, setData] = useState(null);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);

    const load = async () => {
        try {
            const r = await api.get("/reports/expenses", { params: { period_from: from || undefined, period_to: to || undefined, kind: kind || undefined, dept: dept || undefined, location: loc || undefined } });
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [from, to, kind, dept, loc]);
    useEffect(() => {
        (async () => {
            setLocations((await api.get("/masters/locations")).data);
            setDepartments((await api.get("/masters/departments")).data);
        })();
    }, []);

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Expenses" sub="Grouped by head. Filter by period, department, location, and direct/indirect."
                right={
                    <>
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white h-8" data-testid="exp-from" />
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white h-8" data-testid="exp-to" />
                        <select value={kind} onChange={(e) => setKind(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white h-8" data-testid="exp-kind"><option value="">All</option><option value="direct">Direct</option><option value="indirect">Indirect</option></select>
                        <select value={dept} onChange={(e) => setDept(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white h-8" data-testid="exp-dept"><option value="">All depts</option>{departments.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}</select>
                        <select value={loc} onChange={(e) => setLoc(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white h-8" data-testid="exp-loc"><option value="">All locations</option>{locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}</select>
                    </>
                } />
            <div className="p-8">
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white border rounded-md p-4" style={{ borderColor: "var(--se-border)" }}><div className="text-[10px] uppercase tracking-widest text-stone-500">Direct</div><div className="font-mono text-xl mt-1">₹ {inr(data?.total_direct)}</div></div>
                    <div className="bg-white border rounded-md p-4" style={{ borderColor: "var(--se-border)" }}><div className="text-[10px] uppercase tracking-widest text-stone-500">Indirect</div><div className="font-mono text-xl mt-1">₹ {inr(data?.total_indirect)}</div></div>
                    <div className="bg-white border rounded-md p-4" style={{ borderColor: "var(--se-border)" }}><div className="text-[10px] uppercase tracking-widest text-stone-500">Total</div><div className="font-mono text-xl mt-1">₹ {inr(data?.total)}</div></div>
                </div>
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="expenses-table">
                        <thead><tr><th>Head</th><th>Group</th><th>Kind</th><th className="text-right">Amount</th></tr></thead>
                        <tbody>
                            {data?.items?.length ? data.items.map((r) => (
                                <tr key={r.ledger_id}>
                                    <td><Link to={`/ledgers/${r.ledger_id}`} className="hover:underline" data-testid={`expense-${r.ledger_id}`}>{r.ledger_name}</Link></td>
                                    <td className="text-xs text-stone-500">{r.group_name}</td>
                                    <td><span className={`se-chip ${r.is_direct ? "se-chip-warn" : "se-chip-draft"}`}>{r.is_direct ? "direct" : "indirect"}</span></td>
                                    <td className="se-num">₹ {inr(r.amount)}</td>
                                </tr>
                            )) : <tr><td colSpan="4" className="text-stone-500 py-8 text-center">No expenses for this filter.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
